/**
 * The words over a round's calls, and how a call's row shows what it was
 * given. Pure, so they are tested rather than read off a reply.
 */

import { COMPUTER_TOOL_PREFIX } from '@wire';
import type { ToolRun } from '../api';

const COMMANDS = new Set(['run_command', 'run_code']);

/** One of the desktop's computer-use calls. */
export const isComputerCall = (c: ToolRun): boolean => c.tool.startsWith(COMPUTER_TOOL_PREFIX);

const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Over more than one call: "Ran 3 commands", "Ran 2 commands, used 1 tool
 *  (1 failed)", "Asked 3 agents", "Used the computer 12 times, 5 screenshots". */
export function groupLabel(calls: readonly ToolRun[]): string {
  const commands = calls.filter((c) => COMMANDS.has(c.tool)).length;
  const agents = calls.filter((c) => c.tool === 'delegate').length;
  const computer = calls.filter(isComputerCall);
  const tools = calls.length - commands - agents - computer.length;
  const failed = calls.filter((c) => c.state === 'failed').length;
  const shots = computer.reduce((n, c) => n + (c.files?.length ?? 0), 0);
  const parts = [
    commands ? `ran ${count(commands, 'command', 'commands')}` : '',
    agents ? `asked ${count(agents, 'agent', 'agents')}` : '',
    computer.length ? `used the computer ${count(computer.length, 'time', 'times')}` : '',
    shots ? count(shots, 'screenshot', 'screenshots') : '',
    tools ? `used ${count(tools, 'tool', 'tools')}` : ''
  ].filter(Boolean);
  const label = parts.join(', ');
  return `${label[0]!.toUpperCase()}${label.slice(1)}${failed ? ` (${failed} failed)` : ''}`;
}

/** What a row says of a call's state. The page runs its own calls the
 *  moment the reply stops for them, and a Research job and agents run on
 *  their own. */
export function stateText(c: ToolRun): string {
  if (c.state === 'waiting' && c.where === 'page') return 'Running';
  if (c.state === 'waiting' && c.tool === 'research') return 'Researching';
  if (c.state === 'waiting' && c.tool === 'wait_agents') return 'Working';
  if ((c.state === 'waiting' || c.state === 'running') && c.tool === 'generate_video') return 'Making video';
  return STATE_TEXT[c.state];
}

const STATE_TEXT: Record<ToolRun['state'], string> = {
  running: 'Running',
  waiting: 'Waiting for the desktop app',
  approval: 'Waiting for you',
  done: 'Done',
  denied: 'Not run',
  failed: 'Failed'
};

/** Whether a call has finished, one way or another. */
export const settled = (c: ToolRun): boolean => c.state === 'done' || c.state === 'failed' || c.state === 'denied';

/** The language a call's input is coloured as: a shell for a command, the
 *  code's own for code, and arguments as JSON. */
export function inputLanguage(c: ToolRun): string {
  if (c.tool === 'run_command') return 'bash';
  if (c.tool === 'run_code') return /javascript/i.test(c.label) ? 'javascript' : 'python';
  if (c.tool === 'delegate') return '';
  return 'json';
}
